import { useState } from 'react';
import { Toggle } from '@/components/ui';
import { Icon } from '@/components/ui/Icon';
import type { CampaignOverviewCard } from '@/shared/api/adminApi';
import { CountUp } from '@/components/ui/CountUp';
import './campaign.css';

/** DD.MM.YY from an ISO/date string. */
export function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${String(d.getFullYear()).slice(-2)}`;
}

const STATUS_LABEL: Record<string, string> = { draft: 'טיוטה', active: 'פעיל', paused: 'מושהה', completed: 'הסתיים' };
const STATUS_DOT: Record<string, string> = { active: 'var(--ok)', paused: 'var(--warn)', draft: 'var(--faint)', completed: 'var(--faint)' };

export interface CampaignCardProps {
  card: CampaignOverviewCard;
  /** מיקום ברשת — להשהיית הכניסה המדורגת. */
  index?: number;
  joined: boolean;
  dialing: boolean;
  busy: boolean;
  onJoinDial: () => void;
  onLeave: () => void;
  onToggle: (next: boolean) => void;
  onOpen: () => void;
  onLeads: () => void;
  onAgents: () => void;
  onExport: () => void;
  onReturnCapped: () => void;
  onRestart: () => void;
}

/**
 * כרטיס קמפיין לפי GoCall v2: כותרת עם נקודת סטטוס ומתג, 4 מונים (שיחות לנציג
 * מודגש ברקע כחול רך), טבלת משפך, וכפתורי soft בתחתית (הגדרות / לידים / הקדמה).
 * פעולות המשנה (נציגים, ייצוא, הפעלה מחדש) בתפריט "עוד"; חיוג/עזיבה ליד המתג.
 */
export function CampaignCard({ card, index = 0, joined, dialing, busy, onJoinDial, onLeave, onToggle, onOpen, onLeads, onAgents, onExport, onReturnCapped, onRestart }: CampaignCardProps) {
  const capped = card.metrics.capped_leads ?? 0;
  const m = card.metrics;
  const active = card.active;
  const [menuOpen, setMenuOpen] = useState(false);

  const big: { v: number; l: string; hl?: boolean; pill?: string }[] = [
    { v: m.active_leads, l: 'לידים פעילים', pill: m.new_today > 0 ? `+${m.new_today} היום` : undefined },
    { v: m.lines_per_agent, l: 'שיחות לנציג', hl: true },
    { v: m.waiting_today, l: 'ממתינים להיום' },
    { v: m.followups_today, l: 'מעקב להיום' },
  ];

  return (
    <div className={`card camp-card cp-card${active ? '' : ' inactive'}`} style={{
      padding: '22px 22px 16px', gap: 18, overflow: 'visible', border: '1px solid var(--glass-line)',
      // כניסה מדורגת (העיצוב: gcUp .6s, השהיה 0.05+i*0.07). מפתח יציב — polling לא מריץ מחדש.
      animation: `gcUp .6s var(--ease-out) ${0.05 + index * 0.07}s backwards`,
    }}>
      {/* כותרת: נקודה + שם + מטא, ומשמאל חיוג/עזיבה + מתג */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 17, fontWeight: 700, letterSpacing: -0.2 }}>
            <span style={{ width: 8, height: 8, borderRadius: 'var(--r-pill)', flex: 'none', background: STATUS_DOT[card.status] ?? 'var(--faint)' }} />
            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: active ? 'var(--text)' : 'var(--text-2)' }}>{card.name}</span>
          </span>
          <span style={{ fontSize: 12, color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
            {STATUS_LABEL[card.status] ?? card.status} · נוצר <span dir="ltr">{fmtDate(card.created_at)}</span> ·
            <span title="מזהה הקמפיין (campaign_id ב-API)" dir="ltr" style={{ fontVariantNumeric: 'tabular-nums' }}>ID {card.id}</span>
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 2, flex: 'none' }}>
          {active && (
            <button
              type="button"
              className="icon-btn accent"
              onClick={onJoinDial}
              disabled={dialing}
              aria-label={joined ? 'חייג סבב' : 'הצטרף לקמפיין וחייג'}
              title={joined ? 'חייג סבב לידים (אתה מצורף לקמפיין)' : 'הצטרף לקמפיין וחייג — השיחה תגיע לסופטפון שלך'}
            >
              <Icon name="phoneOut" size={18} />
            </button>
          )}
          {joined && (
            <button type="button" className="icon-btn" onClick={onLeave} aria-label="עזוב את הקמפיין" title="עזוב את הקמפיין — הסר את עצמך מהנציגים">
              <Icon name="close" size={15} />
            </button>
          )}
          {/* 🔴 קמפיין שהסתיים אינו חוזר לפעיל במתג: השרת מתיר לו לעלות
              ל-paused בלבד, דרך "הפעל מחדש" שגם מחזיר את הלידים לתור.
              מתג פעיל כאן הציע פעולה שהשרת דוחה ב-422 (קמפיין 26).

              ⚠️ ההסבר יושב ב-title ולא ב-label של ה-Toggle: label מרונדר
              כטקסט גלוי לצד המתג, ומשפט שלם שם מרסק את כותרת הכרטיס. */}
          <span className="cp-press" style={{ marginInlineStart: 4 }} title={card.status === 'completed' ? 'הקמפיין הסתיים — להפעלה מחדש יש ללחוץ "הפעל מחדש"' : undefined}>
            <Toggle checked={active} onChange={onToggle} disabled={card.status === 'completed'} />
          </span>
        </div>
      </div>

      {/* 4 מונים עיקריים */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 4 }}>
        {big.map((b) => (
          <div key={b.l} style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '10px 6px', borderRadius: 10, background: b.hl ? 'var(--accent-soft)' : 'transparent' }}>
            <span dir="ltr" style={{ fontSize: 24, fontWeight: 300, letterSpacing: -0.8, lineHeight: 1.05, fontVariantNumeric: 'tabular-nums', textAlign: 'end', color: b.hl ? 'var(--accent-ink)' : 'var(--text)' }}>
              <CountUp value={b.v} format={fmtInt} />
            </span>
            <span style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.3 }}>{b.l}</span>
            {b.pill && (
              <span style={{ alignSelf: 'flex-start', fontSize: 10.5, color: 'var(--ok)', background: 'var(--ok-soft)', borderRadius: 'var(--r-pill)', padding: '0 6px' }}>{b.pill}</span>
            )}
          </div>
        ))}
      </div>

      {/* לידים חסומים (תקרת ניסיונות / תקרה יומית / סולם) — מוצג רק כשיש כאלה. */}
      {capped > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, background: 'var(--danger-soft)' }}>
          <span style={{ width: 7, height: 7, borderRadius: 'var(--r-pill)', background: 'var(--danger)', animation: 'gcBlink 1.4s ease-in-out infinite', flex: 'none' }} />
          <span style={{ flex: 1, fontSize: 12.5 }}>{capped} לידים חסומים בתקרה</span>
          <button
            type="button"
            className="btn dark"
            style={{ height: 28, padding: '0 12px', fontSize: 12, borderRadius: 7 }}
            onClick={onReturnCapped}
            disabled={busy}
            aria-label={`החזרת ${capped} לידים שחרגו מהתקרה`}
            title={`${capped} לידים חסומים (מיצו ניסיונות / תקרה יומית / ממתינים לסולם) — החזרה לתור`}
          >
            הקדמת לידים
          </button>
        </div>
      )}

      {/* טבלת המשפך */}
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
        {card.stats.map((s) => {
          const deals = s.key === 'deals';
          return (
            <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderTop: '1px solid var(--glass-line-2)', fontSize: 13.5 }}>
              <span style={{ flex: 1, color: 'var(--text-2)' }}>{s.label}</span>
              <span dir="ltr" style={{ fontSize: 12, color: 'var(--muted)', fontVariantNumeric: 'tabular-nums' }}>({s.pct}%)</span>
              <span dir="ltr" style={{ minWidth: 44, textAlign: 'end', fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: deals ? 'var(--ok)' : 'var(--text)' }}><CountUp value={s.value} format={fmtInt} /></span>
            </div>
          );
        })}
      </div>

      {/* כפתורי תחתית */}
      <div style={{ display: 'flex', gap: 6, borderTop: '1px solid var(--glass-line-2)', paddingTop: 12, position: 'relative' }}>
        <button type="button" className="btn soft" style={softBtn} onClick={onOpen} aria-label="אפשרויות ועריכה" title="הגדרות הקמפיין">הגדרות</button>
        <button type="button" className="btn soft" style={softBtn} onClick={onLeads}>לידים</button>
        <button
          type="button"
          className="btn soft"
          style={softBtn}
          onClick={onReturnCapped}
          disabled={busy || capped === 0}
          title={capped > 0 ? `החזרת ${capped} לידים חסומים לתור` : 'אין לידים חסומים להחזרה'}
        >
          הקדמה
        </button>
        <button
          type="button"
          className="btn soft"
          style={{ ...softBtn, flex: 'none', width: 34, padding: 0 }}
          onClick={() => setMenuOpen((o) => !o)}
          aria-label="עוד פעולות"
          aria-expanded={menuOpen}
          title="עוד פעולות"
        >
          <Icon name="more" size={16} />
        </button>
        {menuOpen && (
          <>
            <div onClick={() => setMenuOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 30 }} aria-hidden />
            <div role="menu" className="cp-menu" style={{
              position: 'absolute', bottom: 'calc(100% - 4px)', insetInlineEnd: 0, zIndex: 31, minWidth: 220,
              display: 'flex', flexDirection: 'column', padding: 6, gap: 2,
              background: 'var(--glass-2)', border: '1px solid var(--glass-line)', borderRadius: 12, boxShadow: 'var(--shadow-2)',
            }}>
              <MenuItem icon="users" label="ניהול נציגים" onClick={() => { setMenuOpen(false); onAgents(); }} />
              <MenuItem icon="download" label="ייצוא לידים (CSV)" ariaLabel="ייצוא לידים" onClick={() => { setMenuOpen(false); onExport(); }} />
              <MenuItem
                icon="history"
                label="הפעלה מחדש"
                ariaLabel="הפעלת הקמפיין מחדש"
                title="הפעלת הקמפיין מחדש — כל הלידים חוזרים לתור (מלבד מסורבי פנייה)"
                disabled={busy}
                onClick={() => { setMenuOpen(false); onRestart(); }}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const fmtInt = (v: number) => Math.round(v).toLocaleString('en-US');

const softBtn: React.CSSProperties = { flex: 1, height: 34, padding: '0 8px', fontSize: 13 };

function MenuItem({ icon, label, ariaLabel, title, disabled, onClick }: {
  icon: Parameters<typeof Icon>[0]['name']; label: string; ariaLabel?: string; title?: string; disabled?: boolean; onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      className="dash-row"
      aria-label={ariaLabel}
      title={title}
      disabled={disabled}
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', border: 0, background: 'transparent',
        color: 'var(--text)', fontSize: 13, fontFamily: 'inherit', textAlign: 'start', cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1, borderRadius: 7,
      }}
    >
      <Icon name={icon} size={15} color="var(--muted)" />
      {label}
    </button>
  );
}
